import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Paper,
  Divider,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MapIcon from '@mui/icons-material/Map';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import ExtensionIcon from '@mui/icons-material/Extension';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import { analyticsService } from '../../services/analyticsService';
import { SEOHead, SEO_PAGES } from '../seo/SEOHead';
import { InstallExtensionButton } from '../common/InstallExtensionButton';
// Animations disabled - using pass-through wrappers
const FadeInOnScroll = ({ children }: { children: React.ReactNode; delay?: number; direction?: string }) => <>{children}</>;
const StaggerContainer = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const staggerItemVariants = {};
const motion = { div: ({ children, ...props }: any) => <div {...props}>{children}</div> };

const screenshots = [
  { src: '/images/screenshots/screenshot-1.png', alt: 'GovToolsPro sidepanel on SAM.gov showing opportunity analysis' },
  { src: '/images/screenshots/screenshot-2.png', alt: 'Document analysis and AI-powered insights' },
  { src: '/images/screenshots/screenshot-3.png', alt: 'Analysis tab with historical award trends' },
  { src: '/images/screenshots/screenshot-4.png', alt: 'Advanced Analytics dashboard with market overview' },
  { src: '/images/screenshots/screenshot-5.png', alt: 'Geographic distribution heat map' },
];

const features = [
  {
    icon: <GpsFixedIcon fontSize="large" />,
    title: 'Go/No-Go Scoring',
    description: 'Instant bid qualification against your company profile. NAICS, set-asides, clearances, certs - all checked automatically.',
    tag: 'NEW',
    tagColor: 'success' as const,
  },
  {
    icon: <NotificationsActiveIcon fontSize="large" />,
    title: 'Recompete Alerts',
    description: 'Find expiring federal contracts before they hit SAM.gov. Track opportunities and get email alerts.',
    tag: 'FREE',
    tagColor: 'primary' as const,
  },
  {
    icon: <ArticleIcon fontSize="large" />,
    title: 'Capability Statement',
    description: 'Generate professional PDF capability statements in one click from your company profile.',
    tag: 'NEW',
    tagColor: 'success' as const,
  },
  {
    icon: <SearchIcon fontSize="large" />,
    title: 'Incumbent Intelligence',
    description: 'Find who currently holds the contract. Research competitors with USASpending data.',
    credits: 2,
  },
  {
    icon: <DocumentScannerIcon fontSize="large" />,
    title: 'Document Analysis',
    description: 'AI extracts requirements, deadlines, and evaluation criteria from RFPs, SOWs, and attachments.',
    credits: '1-3',
  },
  {
    icon: <HandshakeIcon fontSize="large" />,
    title: 'Partner Discovery',
    description: 'Find teaming partners near the place of performance with map view, ratings, and contact info.',
    tag: 'FREE',
    tagColor: 'primary' as const,
  },
];

const steps = [
  {
    icon: <ExtensionIcon sx={{ fontSize: 48 }} />,
    title: 'Install Extension',
    description: 'Add GovToolsPro to Chrome for free. Takes 10 seconds.'
  },
  {
    icon: <TouchAppIcon sx={{ fontSize: 48 }} />,
    title: 'Browse SAM.gov',
    description: 'Navigate to any contract opportunity on SAM.gov as you normally would.'
  },
  {
    icon: <GpsFixedIcon sx={{ fontSize: 48 }} />,
    title: 'Get Your Score',
    description: 'Instantly see if you should pursue the opportunity based on your profile.'
  },
];

const geminiCapabilities = [
  {
    icon: <DocumentScannerIcon fontSize="large" />,
    title: 'Document Understanding',
    description: 'Processes PDFs, Word docs, and spreadsheets to extract requirements, deadlines, and evaluation criteria.'
  },
  {
    icon: <PsychologyIcon fontSize="large" />,
    title: 'Intelligent Analysis',
    description: 'Advanced reasoning analyzes complex solicitations and provides actionable recommendations.'
  },
  {
    icon: <SpeedIcon fontSize="large" />,
    title: 'Real-Time Processing',
    description: 'Get instant analysis as you browse SAM.gov - no waiting for batch processing.'
  },
  {
    icon: <AutoAwesomeIcon fontSize="large" />,
    title: 'Context-Aware Generation',
    description: 'AI-generated proposals and emails tailored to each specific opportunity.'
  },
];

const creditCosts = [
  { feature: 'Document Analysis', credits: '1-3', note: 'varies by size' },
  { feature: 'Incumbent Search', credits: '2', note: 'per search' },
  { feature: 'AI Proposal Draft', credits: '5', note: 'per section' },
  { feature: 'Capability Statement', credits: '5', note: 'per PDF' },
  { feature: 'Go/No-Go Score', credits: 'FREE', note: 'after processing' },
  { feature: 'Recompete Alerts', credits: 'FREE', note: 'unlimited' },
];

export const LandingPage: React.FC = () => {
  console.log('[GovToolsPro] LandingPage rendering...');
  const theme = useTheme();
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  const nextScreenshot = () => {
    setCurrentScreenshot((prev) => (prev + 1) % screenshots.length);
  };

  const prevScreenshot = () => {
    setCurrentScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  // Debug: Test if basic content renders
  console.log('[GovToolsPro] About to return JSX...');

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <SEOHead {...SEO_PAGES.home} />

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a2744 50%, #0a0f1a 100%)',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 8, md: 0 },
          pb: { xs: 8, md: 0 },
        }}
      >
        {/* Animated background grid */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(74, 222, 128, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(74, 222, 128, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInOnScroll>
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 2, fontSize: '0.85rem' }}
                >
                  AI-POWERED BID INTELLIGENCE
                </Typography>
              </FadeInOnScroll>

              <FadeInOnScroll delay={0.1}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mt: 2,
                    mb: 3,
                  }}
                >
                  Know If You Should Bid{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    In 30 Seconds
                  </Box>
                </Typography>
              </FadeInOnScroll>

              <FadeInOnScroll delay={0.2}>
                <Typography
                  variant="h5"
                  sx={{ color: 'text.secondary', mb: 4, fontWeight: 400, lineHeight: 1.6 }}
                >
                  Go/No-Go scoring compares every SAM.gov opportunity against your
                  company profile. Stop wasting time on bids you can't win.
                </Typography>
              </FadeInOnScroll>

              <FadeInOnScroll delay={0.3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    href="https://chromewebstore.google.com/detail/govtoolspro-ai-for-govern/cjmjnmmcpaaclkadamieohplibjjldid"
                    target="_blank"
                    onClick={() => analyticsService.ctaClick('install_extension', 'hero')}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                      },
                    }}
                  >
                    Install Free Extension
                  </Button>
                  <Button
                    href="#how-it-works"
                    variant="outlined"
                    size="large"
                    sx={{ px: 4, py: 1.5, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)' }}
                    onClick={() => analyticsService.ctaClick('see_how_it_works', 'hero')}
                  >
                    Watch Demo
                  </Button>
                </Stack>
              </FadeInOnScroll>

              <FadeInOnScroll delay={0.4}>
                <Stack direction="row" spacing={4} sx={{ mt: 5 }}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PeopleIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        500+
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Active Users
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrendingUpIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        $2.1B
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Opportunities Analyzed
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StarIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        4.9
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Chrome Rating
                    </Typography>
                  </Box>
                </Stack>
              </FadeInOnScroll>
            </Grid>

            <Grid item xs={12} md={6}>
              <FadeInOnScroll direction="right" delay={0.2}>
                {/* Go/No-Go Score Card Mockup */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,0.05) 100%)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 3,
                    p: 4,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'success.main', fontWeight: 600, letterSpacing: 1 }}>
                    BID QUALIFICATION SCORE
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={3} sx={{ my: 2 }}>
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 700,
                        color: 'success.main',
                        fontSize: { xs: '4rem', md: '5rem' },
                        lineHeight: 1,
                      }}
                    >
                      78
                    </Typography>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Strong Fit - Recommend GO
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        3 strengths, 1 gap to address
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, mb: 3 }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '78%' }}
                      transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
                      style={{ height: '100%', backgroundColor: '#22c55e', borderRadius: 4 }}
                    />
                  </Box>
                  <Stack spacing={1.5}>
                    <Typography sx={{ color: 'success.main', fontSize: '0.95rem' }}>
                      ✓ NAICS 541512 matches your profile
                    </Typography>
                    <Typography sx={{ color: 'success.main', fontSize: '0.95rem' }}>
                      ✓ 8(a) set-aside - you're eligible
                    </Typography>
                    <Typography sx={{ color: 'success.main', fontSize: '0.95rem' }}>
                      ✓ Virginia is in your service area
                    </Typography>
                    <Typography sx={{ color: 'warning.main', fontSize: '0.95rem' }}>
                      ⚠ No past performance with DoD
                    </Typography>
                  </Stack>
                </Box>
              </FadeInOnScroll>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box id="how-it-works" sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Typography variant="h2" align="center" sx={{ fontWeight: 700, mb: 2 }}>
              How It Works
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: 'text.secondary', mb: 8, maxWidth: 600, mx: 'auto' }}>
              Get started in minutes. No credit card required.
            </Typography>
          </FadeInOnScroll>

          <StaggerContainer>
            <Grid container spacing={4}>
              {steps.map((step, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={staggerItemVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        textAlign: 'center',
                        p: 4,
                        background: 'rgba(255,255,255,0.02)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          color: 'primary.main',
                          mb: 3,
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: 'rgba(74, 222, 128, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                        }}
                      >
                        {step.icon}
                      </Box>
                      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Step {index + 1}
                      </Typography>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {step.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Typography variant="h2" align="center" sx={{ fontWeight: 700, mb: 2 }}>
              Powerful Features
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: 'text.secondary', mb: 8, maxWidth: 600, mx: 'auto' }}>
              Everything you need to find, qualify, and win government contracts
            </Typography>
          </FadeInOnScroll>

          <StaggerContainer>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div variants={staggerItemVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        p: 3,
                        background: 'rgba(255,255,255,0.02)',
                        transition: 'transform 0.3s, border-color 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              color: 'primary.main',
                              bgcolor: 'rgba(74, 222, 128, 0.1)',
                              borderRadius: 2,
                              p: 1.5,
                              display: 'flex',
                            }}
                          >
                            {feature.icon}
                          </Box>
                          {feature.tag && (
                            <Chip
                              label={feature.tag}
                              color={feature.tagColor}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                          {feature.credits && (
                            <Chip
                              label={`${feature.credits} credits`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </Container>
      </Box>

      {/* Recompete Alerts Section */}
      <Box
        sx={{
          py: 12,
          background: 'linear-gradient(180deg, #111827 0%, #1f2937 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <FadeInOnScroll>
                <Chip
                  label="FREE FEATURE"
                  color="primary"
                  sx={{ mb: 2, fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                  Find Expiring Contracts{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    Before Anyone Else
                  </Box>
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Competitors charge $500+/month for recompete data. We give it to you free.
                  Search by your NAICS codes, track contracts, and get email alerts when
                  opportunities are about to expire.
                </Typography>
                <Stack spacing={2}>
                  {[
                    'Auto-populate search from your profile',
                    'Map view with state-level clustering',
                    'Email alerts at 30 and 7 days',
                    'See how many others are watching',
                  ].map((item, index) => (
                    <Stack key={index} direction="row" alignItems="center" spacing={2}>
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                      <Typography>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </FadeInOnScroll>
            </Grid>
            <Grid item xs={12} md={7}>
              <FadeInOnScroll direction="right">
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                    p: 3,
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <NotificationsActiveIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Recompete Alerts</Typography>
                    <Chip label="3 tracked" size="small" color="success" />
                  </Stack>
                  {/* Mock contract list */}
                  {[
                    { title: 'IT Support Services - VA', value: '$2.4M', days: 45, state: 'VA' },
                    { title: 'Cybersecurity Assessment', value: '$850K', days: 23, state: 'MD' },
                    { title: 'Cloud Migration Support', value: '$1.2M', days: 67, state: 'DC' },
                  ].map((contract, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        borderRadius: 2,
                        mb: 2,
                        border: '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography sx={{ fontWeight: 500 }}>{contract.title}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {contract.value} • {contract.state}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${contract.days} days`}
                          size="small"
                          color={contract.days < 30 ? 'warning' : 'default'}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </FadeInOnScroll>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Powered by Gemini AI */}
      <Box sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
                <AutoAwesomeIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Typography variant="h2" sx={{ fontWeight: 700 }}>
                  Powered by Gemini AI
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}>
                Google's most capable AI model brings advanced document understanding and intelligent analysis to government contracting
              </Typography>
            </Box>
          </FadeInOnScroll>

          <StaggerContainer>
            <Grid container spacing={4}>
              {geminiCapabilities.map((capability, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div variants={staggerItemVariants}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          color: 'secondary.main',
                          mb: 2,
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: 'rgba(96, 165, 250, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                        }}
                      >
                        {capability.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {capability.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {capability.description}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Typography variant="h2" align="center" sx={{ fontWeight: 700, mb: 2 }}>
              Simple, Pay-As-You-Go Pricing
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: 'text.secondary', mb: 8, maxWidth: 600, mx: 'auto' }}>
              Start free. Buy credits when you need AI-powered features.
              No subscriptions. No commitments.
            </Typography>
          </FadeInOnScroll>

          <Grid container spacing={4} justifyContent="center">
            {/* Free Tier */}
            <Grid item xs={12} md={4}>
              <FadeInOnScroll delay={0.1}>
                <Card sx={{ height: '100%', p: 4 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    FREE FOREVER
                  </Typography>
                  <Typography variant="h2" sx={{ my: 2, fontWeight: 700 }}>
                    $0
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Everything you need to get started
                  </Typography>
                  <Divider sx={{ my: 3 }} />
                  <Stack spacing={2}>
                    {[
                      'Opportunity tracking & dashboard',
                      'Company profile management',
                      'Recompete alerts & tracking',
                      'Partner discovery with map',
                      'Basic analytics & trends',
                      '10 free credits to start',
                    ].map((item, index) => (
                      <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    href="https://chromewebstore.google.com/detail/govtoolspro-ai-for-govern/cjmjnmmcpaaclkadamieohplibjjldid"
                    target="_blank"
                    sx={{ mt: 4 }}
                  >
                    Install Extension
                  </Button>
                </Card>
              </FadeInOnScroll>
            </Grid>

            {/* Credits */}
            <Grid item xs={12} md={4}>
              <FadeInOnScroll delay={0.2}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    position: 'relative',
                  }}
                >
                  <Chip
                    label="MOST POPULAR"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    CREDIT PACKS
                  </Typography>
                  <Typography variant="h2" sx={{ my: 2, fontWeight: 700 }}>
                    From $10
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Pay only for AI features you use
                  </Typography>
                  <Divider sx={{ my: 3 }} />
                  <Stack spacing={2}>
                    {[
                      'Document analysis (1-3 credits)',
                      'Incumbent search (2 credits)',
                      'Go/No-Go scoring (included)',
                      'AI proposal drafting (5 credits)',
                      'Capability statement PDF (5 credits)',
                      'Credits never expire',
                    ].map((item, index) => (
                      <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 4 }}
                  >
                    Buy Credits
                  </Button>
                </Card>
              </FadeInOnScroll>
            </Grid>

            {/* Enterprise */}
            <Grid item xs={12} md={4}>
              <FadeInOnScroll delay={0.3}>
                <Card sx={{ height: '100%', p: 4 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    ENTERPRISE
                  </Typography>
                  <Typography variant="h2" sx={{ my: 2, fontWeight: 700 }}>
                    Custom
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    For teams and high-volume users
                  </Typography>
                  <Divider sx={{ my: 3 }} />
                  <Stack spacing={2}>
                    {[
                      'Unlimited credits',
                      'Team management',
                      'Priority support',
                      'Custom integrations',
                      'Dedicated success manager',
                      'SLA guarantees',
                    ].map((item, index) => (
                      <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mt: 4 }}
                  >
                    Contact Sales
                  </Button>
                </Card>
              </FadeInOnScroll>
            </Grid>
          </Grid>

          {/* Credit Costs Table */}
          <FadeInOnScroll delay={0.4}>
            <Box sx={{ mt: 8, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 4 }}>
                Credit Costs
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {creditCosts.map((item, index) => (
                  <Grid item xs={6} sm={4} md={2} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <Typography variant="h6" sx={{ color: item.credits === 'FREE' ? 'success.main' : 'primary.main', fontWeight: 700 }}>
                        {item.credits}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.feature}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item.note}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </FadeInOnScroll>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 12,
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <FadeInOnScroll>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: '#0a0f1a' }}>
              Ready to Win More Contracts?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'rgba(0,0,0,0.7)' }}>
              Join 500+ contractors using GovToolsPro to find and win government opportunities.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://chromewebstore.google.com/detail/govtoolspro-ai-for-govern/cjmjnmmcpaaclkadamieohplibjjldid"
              target="_blank"
              onClick={() => analyticsService.ctaClick('install_extension', 'footer_cta')}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                bgcolor: '#0a0f1a',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1a2744',
                },
              }}
            >
              Install Free Extension
            </Button>
          </FadeInOnScroll>
        </Container>
      </Box>

      {/* Disclaimer */}
      <Box sx={{ py: 4, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center" sx={{ opacity: 0.7 }}>
            <strong>Disclaimer:</strong> GovToolsPro provides analytics based on publicly available government data sources including SAM.gov, USAspending.gov, and FPDS.gov. Information presented is for informational purposes only and may not be accurate, complete, or current. Users are solely responsible for conducting their own due diligence before making any business decisions. GovToolsPro is not affiliated with any government agency.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
